import { useEffect, useState, useMemo } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, Search, Trash2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import PosterLightbox, { posterSrc } from "@/components/PosterLightbox";

export default function MRHistory() {
  const [posters, setPosters] = useState([]);
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [viewing, setViewing] = useState(null); // poster open in the lightbox
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/posters").then(({ data }) => setPosters(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => posters.filter((p) => {
    if (p.deleted) return false;
    const okQ = !q || p.doctor_name.toLowerCase().includes(q.toLowerCase()) || p.hospital?.toLowerCase().includes(q.toLowerCase());
    const okD = !date || p.created_at?.startsWith(date);
    return okQ && okD;
  }), [posters, q, date]);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this poster record?")) return;
    try {
      await api.delete(`/posters/${id}`);
      setPosters((prev) => prev.map((p) => p.id === id ? { ...p, deleted: true } : p));
      toast.success("Poster record removed");
    } catch {
      toast.error("Failed to delete poster");
    }
  };

  const handleDownload = async (poster) => {
    if (downloading) return;
    setDownloading(poster.id);
    try {
      const res = await fetch(`${API}/posters/${poster.id}/download?fmt=jpg`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${poster.doctor_name.replace(/ /g, "_")}_WHD.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      // Local-disk posters are removed after the first download; CDN copies stay.
      if (!poster.poster_url) {
        setPosters((prev) => prev.map((p) => p.id === poster.id ? { ...p, file_deleted: true } : p));
      }
      toast.success("Download successful");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Shell role="mr">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">History</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Your Generated Posters</h1>
      </div>

      <Card className="p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="history-search"
            placeholder="Search by doctor name or hospital…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
        <Input data-testid="history-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 md:w-48" />
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed text-slate-500">No posters found.</Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} className="overflow-hidden" data-testid={`history-card-${p.id}`}>
              <div className="aspect-square bg-slate-100 relative">
                {p.file_deleted ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400">
                    <ImageOff className="w-8 h-8" />
                    <span className="text-xs text-center px-2">File removed<br/>from server</span>
                  </div>
                ) : (
                  <button type="button" onClick={() => setViewing(p)} className="w-full h-full block focus:outline-none" title="View poster">
                    <img src={posterSrc(p)} alt={p.doctor_name} loading="lazy" className="w-full h-full object-cover object-top hover:opacity-90 transition" />
                  </button>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="text-sm font-semibold text-slate-900 truncate">{p.doctor_name}</div>
                <div className="text-xs text-slate-500 truncate">{p.hospital}</div>
                <div className="text-[11px] text-slate-400">{new Date(p.created_at).toLocaleString()}</div>
                {!p.file_deleted && (
                  <div className="pt-1">
                    <Button size="sm" variant="outline" className="w-full h-8 gap-1 text-xs" onClick={() => handleDownload(p)} disabled={downloading === p.id}>
                      <Download className="w-3 h-3"/> Download Poster
                    </Button>
                  </div>
                )}
                <Button
                  size="sm" variant="ghost"
                  className="w-full h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 gap-1"
                  onClick={() => handleDelete(p.id)}
                >
                  <Trash2 className="w-3 h-3"/> Remove record
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <PosterLightbox
        poster={viewing}
        onClose={() => setViewing(null)}
        onDownload={handleDownload}
        downloading={viewing && downloading === viewing.id}
      />
    </Shell>
  );
}
